/**
 * GlobalView — Natural Event Detail Popup
 *
 * Overlay showing EONET natural event details for the selected entity.
 * Shows event title, category badge, magnitude (if available), date,
 * coordinates, and source link. Draggable via header.
 * Follows existing popup pattern (EarthquakePopup).
 */

import { useEffect, useRef } from 'react';
import { useGlobeStore } from '../../stores/globeStore';
import { useEntityStore } from '../../stores/entityStore';
import { categoryToColor, categoryToIcon } from '../../services/eonetClient';
import { useDraggable } from '../../hooks/useDraggable';
import { useIsMobile } from '../../hooks/useIsMobile';
import EntityBottomSheet from './EntityBottomSheet';
import styles from './NaturalEventPopup.module.css';

// ─── Formatters ──────────────────────────────────────────────────

function timeAgo(isoStr: string): string {
  const diff = (Date.now() - new Date(isoStr).getTime()) / 1000;
  if (diff < 60) return `vor ${Math.round(diff)}s`;
  if (diff < 3600) return `vor ${Math.round(diff / 60)}min`;
  if (diff < 86400) return `vor ${(diff / 3600).toFixed(1)}h`;
  return `vor ${Math.round(diff / 86400)}d`;
}

function fmtTime(isoStr: string): string {
  const d = new Date(isoStr);
  return d.toLocaleString('de-CH', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    timeZoneName: 'short',
  });
}

function fmtCoord(val: number, decimals = 4): string {
  return val.toFixed(decimals);
}

// ─── Shared Content ──────────────────────────────────────────────

interface NaturalEventContentProps {
  entity: {
    position: { lat: number; lon: number; alt: number };
    timestamp: string;
    properties: Record<string, unknown>;
  };
}

function NaturalEventContent({ entity }: NaturalEventContentProps) {
  const props = entity.properties;
  const title = (props.title as string) ?? 'Unbekanntes Ereignis';
  const description = props.description as string | null | undefined;
  const categoryId = (props.categoryId as string) ?? '';
  const categoryTitle = (props.categoryTitle as string) ?? 'Unbekannt';
  const magnitudeValue = props.magnitudeValue as number | null | undefined;
  const magnitudeUnit = props.magnitudeUnit as string | null | undefined;
  const sourceUrl = props.sourceUrl as string | undefined;
  const sourceId = props.sourceId as string | undefined;
  const link = props.link as string | undefined;

  const catColor = categoryToColor(categoryId);
  const catIcon = categoryToIcon(categoryId);

  return (
    <>
      {/* Category badge */}
      <span
        className={styles.categoryBadge}
        style={{ borderColor: `${catColor}55`, color: catColor }}
      >
        {catIcon} {categoryTitle}
      </span>

      {/* Magnitude (if present) */}
      {magnitudeValue != null && (
        <div className={styles.magBadge}>
          <span className={styles.magValue} style={{ color: catColor }}>
            {typeof magnitudeValue === 'number'
              ? magnitudeValue.toLocaleString('de-CH', { maximumFractionDigits: 1 })
              : magnitudeValue}
          </span>
          {magnitudeUnit && (
            <span className={styles.magUnit}>{magnitudeUnit}</span>
          )}
        </div>
      )}

      {/* Description */}
      {description && (
        <div className={styles.description}>{description}</div>
      )}

      {/* Title (full) */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>EVENT</div>
        <div className={styles.description} style={{ marginBottom: 0 }}>
          {title}
        </div>
      </div>

      {/* Location */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>ORT</div>
        <div className={styles.grid}>
          <Row label="LAT" value={fmtCoord(entity.position.lat)} suffix={entity.position.lat >= 0 ? '° N' : '° S'} />
          <Row label="LON" value={fmtCoord(entity.position.lon)} suffix={entity.position.lon >= 0 ? '° E' : '° W'} />
        </div>
      </div>

      {/* Time */}
      <div className={styles.section}>
        <div className={styles.sectionTitle}>ZEIT</div>
        <div className={styles.grid}>
          <Row label="UTC" value={fmtTime(entity.timestamp)} />
          <Row label="AGO" value={timeAgo(entity.timestamp)} />
        </div>
      </div>

      {/* Source link */}
      {(sourceUrl || link) && (
        <a
          href={sourceUrl || link}
          target="_blank"
          rel="noopener noreferrer"
          className={styles.sourceLink}
        >
          🔗 {sourceId ? `QUELLE: ${sourceId}` : 'EONET EVENT'}
        </a>
      )}
    </>
  );
}

// ─── Component ───────────────────────────────────────────────────

export default function NaturalEventPopup() {
  const selectedEntityId = useGlobeStore((s) => s.selectedEntityId);
  const setSelectedEntity = useGlobeStore((s) => s.setSelectedEntity);
  const entity = useEntityStore((s) =>
    selectedEntityId ? s.entities.get(selectedEntityId) : undefined,
  );
  const isMobile = useIsMobile();

  const { style: dragStyle, onMouseDown, isDragging, reset } = useDraggable(296, 32);
  const prevEntityRef = useRef<string | null>(null);

  useEffect(() => {
    if (selectedEntityId && selectedEntityId !== prevEntityRef.current) {
      reset();
      prevEntityRef.current = selectedEntityId;
    }
  }, [selectedEntityId, reset]);

  useEffect(() => {
    if (!selectedEntityId) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSelectedEntity(null);
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [selectedEntityId, setSelectedEntity]);

  if (!selectedEntityId || !entity || entity.type !== 'natural_event') return null;

  const props = entity.properties as Record<string, unknown>;
  const title = (props.title as string) ?? 'Naturereignis';
  const categoryId = (props.categoryId as string) ?? '';
  const catIcon = categoryToIcon(categoryId);

  const handleClose = () => setSelectedEntity(null);

  const contentEntity = {
    position: entity.position,
    timestamp: entity.timestamp,
    properties: props,
  };

  const desktopPopup = (
    <div
      className={styles.popup}
      style={dragStyle}
      id="natural-event-popup"
      data-draggable
      onMouseDown={(e) => e.stopPropagation()}
    >
      <div
        className={styles.header}
        onMouseDown={onMouseDown}
        style={{ cursor: isDragging ? 'grabbing' : 'grab' }}
      >
        <div className={styles.headerLeft}>
          <span className={styles.headerIcon}>{catIcon}</span>
          <span className={styles.headerTitle}>
            {title.length > 28 ? title.slice(0, 28) + '…' : title}
          </span>
        </div>
        <button className={styles.closeBtn} onClick={handleClose} title="Schliessen (ESC)" id="natural-event-popup-close">✕</button>
      </div>
      <NaturalEventContent entity={contentEntity} />
    </div>
  );

  if (isMobile) {
    return (
      <EntityBottomSheet
        visible={true}
        entityName={title.length > 20 ? title.slice(0, 20) + '…' : title}
        entitySub={(props.categoryTitle as string) ?? ''}
        icon={catIcon}
        onClose={handleClose}
        desktopPopup={desktopPopup}
      >
        <NaturalEventContent entity={contentEntity} />
      </EntityBottomSheet>
    );
  }

  return desktopPopup;
}

// ─── Row Helper ──────────────────────────────────────────────────

function Row({ label, value, suffix }: { label: string; value: string; suffix?: string }) {
  return (
    <div className={styles.row}>
      <span className={styles.label}>{label}</span>
      <span className={styles.value}>
        {value}{suffix && <span className={styles.suffix}>{suffix}</span>}
      </span>
    </div>
  );
}
